import React, { useState, useEffect, useRef, useCallback } from "react";

interface ModernPlaceAutocompleteProps {
  value: string;
  placeholder?: string;
  className?: string;
  onChange: (value: string) => void;
  onPlaceSelect: (address: string, lat: string, lng: string) => void;
  disabled?: boolean;
}

interface SuggestionItem {
  id: string;
  mainText: string;
  secondaryText?: string;
  fullText: string;
  rawSuggestion?: any;
}

export const ModernPlaceAutocomplete: React.FC<ModernPlaceAutocompleteProps> = ({
  value,
  placeholder = "Search location...",
  className = "form-control",
  onChange,
  onPlaceSelect,
  disabled = false,
}) => {
  const [inputValue, setInputValue] = useState(value || "");
  const [suggestions, setSuggestions] = useState<SuggestionItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const sessionTokenRef = useRef<any>(null);
  const debounceTimerRef = useRef<any>(null);

  // Sync internal input value with external prop
  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // Fetch suggestions using modern Places API (New) or fallback to Geocoder
  const fetchSuggestions = useCallback(async (query: string) => {
    if (!query || query.trim().length < 2) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    if (!window.google || !window.google.maps) {
      return;
    }

    setIsLoading(true);

    try {
      // 1. Try modern Places API (New) AutocompleteSuggestion
      if (google.maps.importLibrary) {
        try {
          const { AutocompleteSuggestion, AutocompleteSessionToken } =
            (await google.maps.importLibrary("places")) as any;

          if (!sessionTokenRef.current && AutocompleteSessionToken) {
            sessionTokenRef.current = new AutocompleteSessionToken();
          }

          if (AutocompleteSuggestion?.fetchAutocompleteSuggestions) {
            const request: any = {
              input: query,
              sessionToken: sessionTokenRef.current,
            };

            const response = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
            if (response && response.suggestions && response.suggestions.length > 0) {
              const formatted: SuggestionItem[] = response.suggestions.map(
                (item: any, idx: number) => {
                  const prediction = item.placePrediction;
                  const fullText =
                    prediction?.text?.text ||
                    prediction?.structuredFormat?.mainText?.text ||
                    query;
                  const mainText =
                    prediction?.structuredFormat?.mainText?.text || fullText;
                  const secondaryText =
                    prediction?.structuredFormat?.secondaryText?.text || "";

                  return {
                    id: prediction?.placeId || String(idx),
                    mainText,
                    secondaryText,
                    fullText: secondaryText ? `${mainText}, ${secondaryText}` : mainText,
                    rawSuggestion: item,
                  };
                }
              );

              setSuggestions(formatted);
              setIsOpen(true);
              setIsLoading(false);
              return;
            }
          }
        } catch (newPlacesErr) {
          console.warn("Places API (New) suggestion fetch failed, trying Geocoder fallback:", newPlacesErr);
        }
      }

      // 2. Fallback: Google Maps Geocoder (Always enabled and reliable)
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: query }, (results, status) => {
        setIsLoading(false);
        if (status === google.maps.GeocoderStatus.OK && results && results.length > 0) {
          const formatted: SuggestionItem[] = results.slice(0, 5).map((r, idx) => ({
            id: r.place_id || String(idx),
            mainText: r.formatted_address.split(",")[0] || r.formatted_address,
            secondaryText: r.formatted_address.split(",").slice(1).join(",").trim(),
            fullText: r.formatted_address,
            rawSuggestion: r,
          }));
          setSuggestions(formatted);
          setIsOpen(true);
        } else {
          setSuggestions([]);
          setIsOpen(false);
        }
      });
    } catch (err) {
      console.error("Location search error:", err);
      setIsLoading(false);
      setSuggestions([]);
      setIsOpen(false);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setInputValue(text);
    onChange(text);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchSuggestions(text);
    }, 250);
  };

  const handleSelectSuggestion = async (item: SuggestionItem) => {
    setInputValue(item.fullText);
    onChange(item.fullText);
    setIsOpen(false);
    setSuggestions([]);

    try {
      // 1. If prediction from Places API (New)
      if (item.rawSuggestion?.placePrediction?.toPlace) {
        try {
          const place = item.rawSuggestion.placePrediction.toPlace();
          await place.fetchFields({
            fields: ["displayName", "formattedAddress", "location"],
            sessionToken: sessionTokenRef.current,
          });

          // Reset session token after completion
          sessionTokenRef.current = null;

          if (place.location) {
            const lat = place.location.lat().toString();
            const lng = place.location.lng().toString();
            const address = place.formattedAddress || place.displayName || item.fullText;
            onPlaceSelect(address, lat, lng);
            return;
          }
        } catch (placeErr) {
          console.warn("Places fetchFields failed, trying Geocoder fallback:", placeErr);
        }
      }

      // 2. If result from Geocoder fallback or if coords need to be resolved
      if (item.rawSuggestion?.geometry?.location) {
        const lat = item.rawSuggestion.geometry.location.lat().toString();
        const lng = item.rawSuggestion.geometry.location.lng().toString();
        onPlaceSelect(item.fullText, lat, lng);
        return;
      }

      // 3. Fallback to Geocoder
      if (window.google?.maps?.Geocoder) {
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({ address: item.fullText }, (results, status) => {
          if (status === google.maps.GeocoderStatus.OK && results && results[0]?.geometry?.location) {
            const lat = results[0].geometry.location.lat().toString();
            const lng = results[0].geometry.location.lng().toString();
            onPlaceSelect(results[0].formatted_address || item.fullText, lat, lng);
          } else {
            onPlaceSelect(item.fullText, "", "");
          }
        });
        return;
      }

      onPlaceSelect(item.fullText, "", "");
    } catch (err) {
      console.error("Failed to resolve place coordinates:", err);
      onPlaceSelect(item.fullText, "", "");
    }
  };

  const handleBlurOrEnter = () => {
    if (!inputValue || inputValue.trim().length < 2) return;
    if (suggestions.length > 0) {
      handleSelectSuggestion(suggestions[0]);
    } else if (window.google?.maps?.Geocoder) {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: inputValue }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK && results && results[0]?.geometry?.location) {
          const lat = results[0].geometry.location.lat().toString();
          const lng = results[0].geometry.location.lng().toString();
          onPlaceSelect(results[0].formatted_address || inputValue, lat, lng);
        }
      });
    }
  };

  return (
    <div className="position-relative w-100" ref={containerRef}>
      <div className="position-relative d-flex align-items-center">
        <input
          type="text"
          className={className}
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleBlurOrEnter();
            }
          }}
          onBlur={() => {
            setTimeout(handleBlurOrEnter, 200);
          }}
          onFocus={() => {
            if (suggestions.length > 0) setIsOpen(true);
          }}
          disabled={disabled}
          autoComplete="off"
        />
        {isLoading && (
          <div
            className="position-absolute end-0 me-3 spinner-border spinner-border-sm text-primary"
            style={{ width: "16px", height: "16px", borderWidth: "2px" }}
            role="status"
          />
        )}
      </div>

      {isOpen && suggestions.length > 0 && (
        <div
          className="position-absolute start-0 end-0 mt-1 shadow-lg border rounded-3 p-1"
          style={{
            zIndex: 1060,
            background: "var(--rm-card-bg, #ffffff)",
            backdropFilter: "blur(18px)",
            WebkitBackdropFilter: "blur(18px)",
            borderColor: "var(--rm-card-border, #e2e8f0)",
            maxHeight: "260px",
            overflowY: "auto",
          }}
        >
          {suggestions.map((item) => (
            <div
              key={item.id}
              className="d-flex align-items-start gap-2 p-2 rounded-2 text-start"
              style={{
                cursor: "pointer",
                transition: "background 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(79, 70, 229, 0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
              onClick={() => handleSelectSuggestion(item)}
            >
              <i
                className="ti ti-map-pin mt-1"
                style={{ color: "var(--rm-primary, #4F46E5)", fontSize: "16px", flexShrink: 0 }}
              />
              <div className="flex-grow-1 overflow-hidden">
                <div
                  className="fw-semibold text-truncate"
                  style={{ fontSize: "0.86rem", color: "var(--rm-text-primary, #0F172A)" }}
                >
                  {item.mainText}
                </div>
                {item.secondaryText && (
                  <div
                    className="small text-truncate"
                    style={{ fontSize: "0.76rem", color: "var(--rm-text-muted, #64748B)" }}
                  >
                    {item.secondaryText}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ModernPlaceAutocomplete;
